<template>
    <div class="slots-container">

        <!-- User Balance Display (Seamless Integration) -->
        <!-- <CasinoBalance v-if="authenticated" /> -->

        <!-- Loading State -->
        <div v-if="loading && providers.length === 0" class="loading-state providers-loading">
            <div class="loading-spinner-simple">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" opacity="0.25"/>
                    <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                        <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite"/>
                    </path>
                </svg>
                <p class="loading-text-simple">Loading providers...</p>
            </div>
        </div>

        <!-- Error State -->
        <div v-else-if="error && providers.length === 0" class="error-state">
            <p class="error-message">{{ error }}</p>
            <ButtonGradient @click="loadProviders" text="Retry" />
        </div>

        <!-- Slots Content: Search + Provider filter + All games grid -->
        <div v-else class="slots-content">
            <div class="games-section">
                <!-- Top bar: Search (left) + Provider dropdown (right) - like reference -->
                <div class="slots-filter-bar">
                    <div class="search-section">
                        <div class="search-input-wrapper">
                            <svg class="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M21 21L16.65 16.65M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                            <input 
                                v-model="searchQuery" 
                                type="text" 
                                placeholder="Search games" 
                                class="search-input"
                            />
                            <button v-if="searchQuery" @click="searchQuery = ''" class="search-clear">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </button>
                        </div>
                    </div>
                    <div class="provider-filter-wrap">
                        <div class="provider-dropdown" :class="{ open: providerDropdownOpen }">
                            <button type="button" class="provider-dropdown-trigger" @click="providerDropdownOpen = !providerDropdownOpen" aria-haspopup="listbox" :aria-expanded="providerDropdownOpen">
                                <span class="provider-label">Provider:</span>
                                <span class="provider-value">{{ selectedProviderLabel }}</span>
                                <svg class="provider-caret" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M6 9L12 15L18 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </button>
                            <div v-show="providerDropdownOpen" class="provider-dropdown-menu" role="listbox">
                                <div 
                                    class="provider-dropdown-item" 
                                    :class="{ active: !selectedProviderFilter }"
                                    role="option"
                                    @click="selectProviderFilter(''); providerDropdownOpen = false"
                                >
                                    All
                                </div>
                                <div 
                                    v-for="opt in providerDropdownList" 
                                    :key="opt"
                                    class="provider-dropdown-item"
                                    :class="{ active: (selectedProviderFilter || '').toLowerCase() === opt }"
                                    role="option"
                                    @click="selectProviderFilter(opt); providerDropdownOpen = false"
                                >
                                    {{ formatProviderOption(opt) }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="gameLoading" class="loading-state games-loading">
                    <div class="loading-spinner-simple">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" opacity="0.25"/>
                            <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                                <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite"/>
                            </path>
                        </svg>
                        <p class="loading-text-simple">Loading games...</p>
                    </div>
                </div>
                <div v-else-if="filteredGames.length === 0" class="no-games">
                    <p v-if="searchQuery">No games found matching "{{ searchQuery }}"</p>
                    <p v-else-if="selectedProviderFilter">No games available for this provider</p>
                    <p v-else>No games available</p>
                </div>
                <div v-else class="games-grid-wrapper">
                    <div class="games-grid">
                        <SlotsGameCard
                            v-for="game in displayedGames"
                            :key="(game.provider_code || '') + '_' + game.code"
                            :game="game"
                            :provider-code="game.provider_code"
                            @launch="launchGame"
                        />
                    </div>
                    <div v-if="hasMoreGames" class="show-more-section">
                        <button type="button" class="show-more-btn" @click="showMoreGames">
                            Show more
                            <span class="show-more-count">(+{{ nextShowMoreCount }} games)</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
        <!-- Game Modal – professional iframe container -->
        <transition name="game-modal-fade">
            <div v-if="gameSession" class="game-modal-overlay" @click="closeGame" role="dialog" aria-modal="true" :aria-label="`Playing ${formattedCurrentGame}. Click outside or press Escape to close.`">
                <div class="game-modal" @click.stop role="document">
                    <header class="game-modal-header">
                        <span class="game-modal-title">{{ formattedCurrentGame }}</span>
                        <button type="button" class="game-modal-close" @click.stop="closeGame" aria-label="Close game">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                            <span class="game-modal-close-text">Close</span>
                        </button>
                    </header>
                    <div class="game-modal-content">
                        <SlotsGameFrame :session="gameSession" />
                    </div>
                    <!-- Floating close – always on top and clickable above iframe -->
                    <button type="button" class="game-modal-close-floating" @click.stop="closeGame" aria-label="Close game">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </button>
                </div>
            </div>
        </transition>

        <!-- Scroll to Top Button - Hidden when game is playing -->
        <button 
            v-show="showScrollTop && !gameSession" 
            @click="scrollToTop" 
            class="scroll-to-top"
            title="Back to Top"
        >
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 19V5M12 5L5 12M12 5L19 12" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
        </button>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import LoadingAnimation from '@/components/LoadingAnimation.vue';
import ButtonGradient from '@/components/ButtonGradient.vue';
import CasinoBalance from './CasinoBalance.vue';
import SlotsGameCard from './SlotsGameCard.vue';
import SlotsGameFrame from './SlotsGameFrame.vue';
import Payment from '@/components/Payment.vue';

export default {
    name: 'SlotsMain',
    components: {
        LoadingAnimation,
        ButtonGradient,
        CasinoBalance,
        SlotsGameCard,
        SlotsGameFrame,
        Payment
    },
    data() {
        return {
            gameLoading: false,
            socketUnsubscribe: null,
            selectedProviderFilter: '', // '' = All, or provider code
            providerDropdownOpen: false,
            allowedProviders: ['SLOTSGATEWAY'],
            searchQuery: '',
            showScrollTop: false,
            mainElement: null,
            gamesDisplayLimit: 21
        };
    },
    computed: {
        ...mapGetters('slots', ['providers', 'games', 'currentProvider', 'currentGame', 'gameSession', 'loading', 'error', 'cryptoPrices']),
        ...mapGetters(['authenticated', 'socketGeneral']),
        filteredProviders() {
            return this.providers.filter(provider => 
                this.allowedProviders.some(allowed => 
                    provider.code.toUpperCase().includes(allowed) || 
                    provider.name.toUpperCase().includes(allowed)
                )
            );
        },
        /** Every provider that appears in the games list (for dropdown: All + each provider) */
        providerOptionsFromGames() {
            const seen = new Set();
            this.games.forEach(g => {
                const p = (g.provider_code || g.provider_name || g.provider || '').toString().trim().toLowerCase();
                if (p) seen.add(p);
            });
            return [...seen].sort((a, b) => a.localeCompare(b));
        },
        /** Dropdown list: use providers from games when available, else store providers */
        providerDropdownList() {
            if (this.providerOptionsFromGames.length > 0) {
                return this.providerOptionsFromGames;
            }
            return this.filteredProviders.map(p => (p.code || p.name || '').toString().trim().toLowerCase()).filter(Boolean);
        },
        filteredGames() {
            let list = this.games;
            if (this.selectedProviderFilter) {
                const want = this.selectedProviderFilter.toLowerCase();
                list = list.filter(game => {
                    const p = (game.provider_code || game.provider_name || game.provider || '').toString().trim().toLowerCase();
                    return p === want;
                });
            }
            if (!this.searchQuery.trim()) {
                return list;
            }
            const query = this.searchQuery.toLowerCase().trim();
            return list.filter(game =>
                (game.name && game.name.toLowerCase().includes(query)) ||
                (game.code && game.code.toLowerCase().includes(query))
            );
        },
        selectedProviderLabel() {
            if (!this.selectedProviderFilter) return 'All';
            return this.formatProviderOption(this.selectedProviderFilter);
        },
        displayedGames() {
            return this.filteredGames.slice(0, this.gamesDisplayLimit);
        },
        hasMoreGames() {
            return this.filteredGames.length > this.gamesDisplayLimit;
        },
        nextShowMoreCount() {
            const remaining = this.filteredGames.length - this.gamesDisplayLimit;
            return Math.min(31, remaining);
        },
        formattedCurrentGame() {
            if (!this.currentGame) return '';
            
            let name = this.currentGame;
            
            // Replace underscores and hyphens with spaces
            name = name.replace(/[_-]/g, ' ');
            
            // Handle camelCase by adding spaces before capital letters
            name = name.replace(/([a-z])([A-Z])/g, '$1 $2');
            
            // Convert to title case (capitalize first letter of each word)
            name = name.toLowerCase().split(' ').map(word => {
                // Keep common abbreviations in uppercase
                const uppercaseWords = ['rtp', 'vip', 'ii', 'iii', 'iv', 'vs', 'xl', 'xxi', 'xxl'];
                if (uppercaseWords.includes(word.toLowerCase())) {
                    return word.toUpperCase();
                }
                // Capitalize first letter of each word
                return word.charAt(0).toUpperCase() + word.slice(1);
            }).join(' ');
            
            // Clean up multiple spaces
            name = name.replace(/\s+/g, ' ').trim();
            
            return name;
        }
    },

        watch: {
        $route: {
            handler(to) {
                this.syncProviderFromRoute();
            },
            immediate: false
        },
        selectedProviderFilter() {
            this.gamesDisplayLimit = 21;
        },
        searchQuery() {
            this.gamesDisplayLimit = 21;
        }
    },

    async mounted() {
        await this.loadProviders();
        await this.fetchCryptoPrices();

        // Load all slot games from all providers for search + filter UX
        const codes = this.filteredProviders.map(p => p.code);
        if (codes.length) {
            this.gameLoading = true;
            await this.fetchAllGames(codes);
            this.gameLoading = false;
        }

        // Sync provider from URL (e.g. /slots vs /slots/provider/PRAGMATIC)
        this.syncProviderFromRoute();

        // Load user balance if authenticated
        if (this.authenticated) {
            await this.fetchUserBalance();
            this.setupSocketListeners();
        }

        // Add scroll event listener for scroll to top button
        window.addEventListener('scroll', this.handleScroll);
        this.mainElement = document.querySelector('main');
        if (this.mainElement) {
            this.mainElement.addEventListener('scroll', this.handleScroll);
        }
        this.handleScroll();
        document.addEventListener('click', this.handleClickOutsideProviderDropdown);
    },

    beforeUnmount() {
        document.removeEventListener('click', this.handleClickOutsideProviderDropdown);
        document.removeEventListener('keydown', this.handleGameModalKeydown);
        if (this.socketUnsubscribe) {
            this.socketUnsubscribe();
        }
        window.removeEventListener('scroll', this.handleScroll);
        if (this.mainElement) {
            this.mainElement.removeEventListener('scroll', this.handleScroll);
        }
        document.body.classList.remove('slot-game-playing');
    },

    beforeDestroy() {
        document.removeEventListener('click', this.handleClickOutsideProviderDropdown);
        window.removeEventListener('scroll', this.handleScroll);
        if (this.mainElement) {
            this.mainElement.removeEventListener('scroll', this.handleScroll);
        }
        document.body.classList.remove('slot-game-playing');
    },

    methods: {
        ...mapActions('slots', ['fetchProviders', 'fetchGamesByProvider', 'fetchAllGames', 'fetchCryptoPrices', 'resetGameSession', 'clearError', 'fetchUserBalance', 'handleBalanceUpdate']),
        
        async loadProviders() {
            await this.fetchProviders();
        },

        selectProviderFilter(code) {
            this.selectedProviderFilter = code || '';
            const path = code ? `/slots/provider/${code}` : '/slots';
            if (this.$route.path !== path) {
                this.$router.push(path).catch(() => {});
            }
        },

        formatProviderName(name) {
            if (!name || typeof name !== 'string') return name || '';
            return name.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
        },

        formatProviderOption(value) {
            if (!value) return '';
            return String(value).toLowerCase().replace(/[_\s]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        },

        async selectDefaultProvider() {
            // Find Pragmatic provider from filtered providers
            const pragmaticProvider = this.filteredProviders.find(provider => 
                provider.code.toUpperCase().includes('PRAGMATIC') || 
                provider.name.toUpperCase().includes('PRAGMATIC')
            );
            
            if (pragmaticProvider) {
                await this.selectProvider(pragmaticProvider.code);
            } else if (this.filteredProviders.length > 0) {
                // Fallback to first available provider if Pragmatic not found
                await this.selectProvider(this.filteredProviders[0].code);
            }
        },

        async selectProvider(providerCode) {
            this.gameLoading = true;
            this.selectedProvider = providerCode;
            this.$store.commit('slots/slots_set_current_provider', providerCode);
            await this.fetchGamesByProvider(providerCode);
            this.gameLoading = false;
        },

        getProviderGameCount(providerCode) {
            return this.games.filter(game => game.provider_code === providerCode).length;
        },

        getCurrentProviderName() {
            const provider = this.providers.find(p => p.code === this.currentProvider);
            const name = provider ? provider.name : 'Games';
            if (!name || typeof name !== 'string') return name;
            return name.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
        },

        clearProvider() {
            this.selectedProvider = '';
            this.searchQuery = '';
            this.$store.commit('slots/slots_set_current_provider', null);
        },

        goToSlotsMain() {
            this.clearProvider();
            this.$router.push('/slots').catch(() => {});
        },

        showMoreGames() {
            this.gamesDisplayLimit += 31;
        },

        goToProviderGames(providerCode) {
            this.selectProvider(providerCode);
            this.$router.push({ name: 'SlotsProvider', params: { providerCode } }).catch(() => {});
        },
        
        getProviderIcon(providerCode) {
            if (!providerCode) return null;
            
            // First check if provider object has icon/image from API
            const provider = this.providers.find(p => p.code === providerCode);
            if (provider && (provider.icon || provider.image || provider.logo)) {
                return provider.icon || provider.image || provider.logo;
            }
            
            // Map provider codes to icon paths from provider_logos folder
            const iconMap = {
                'PRAGMATIC': () => {
                    try { return require('@/assets/img/provider_logos/Pragmatic.jpg'); } catch { return null; }
                },
                'HABANERO': () => {
                    try { return require('@/assets/img/provider_logos/Habanero.webp'); } catch { return null; }
                },
                'BOOONGO': () => {
                    try { return require('@/assets/img/provider_logos/booongo.png'); } catch { return null; }
                },
                'PLAYSON': () => {
                    try { return require('@/assets/img/provider_logos/playson.png'); } catch { return null; }
                },
                'DREAMTECH': () => {
                    try { return require('@/assets/img/provider_logos/dreamtech.png'); } catch { return null; }
                },
                'GAMEART': () => {
                    try { return require('@/assets/img/provider_logos/gameart.jpg'); } catch { return null; }
                },
                'GMW': () => {
                    try { return require('@/assets/img/provider_logos/gmw.png'); } catch { return null; }
                },
                'PLAYSTAR': () => {
                    try { return require('@/assets/img/provider_logos/playstar.jpg'); } catch { return null; }
                },
                'MICROGAMING': () => {
                    try { return require('@/assets/img/provider_logos/microgaming.png'); } catch { return null; }
                },
                'REDTIGER': () => {
                    try { return require('@/assets/img/provider_logos/redtiger.png'); } catch { return null; }
                },
                'NAGA': () => {
                    try { return require('@/assets/img/provider_logos/naga.png'); } catch { return null; }
                },
                'BTG': () => {
                    try { return require('@/assets/img/provider_logos/btg.png'); } catch { return null; }
                },
                'QUICKSPIN': () => {
                    try { return require('@/assets/img/provider_logos/quickspin.png'); } catch { return null; }
                },
                'INOUT': () => {
                    try { return require('@/assets/img/provider_logos/inout.png'); } catch { return null; }
                },
                'CQ9': () => {
                    try { return require('@/assets/img/provider_logos/CQ9.png'); } catch { return null; }
                },
                'EVOPLAY': () => {
                    try { return require('@/assets/img/provider_logos/evoplay.png'); } catch { return null; }
                },
                'PGSOFT': () => {
                    try { return require('@/assets/img/provider_logos/pgsoft.png'); } catch { return null; }
                },
                'BGAMING': () => {
                    try { return require('@/assets/img/provider_logos/BGAMING.webp'); } catch { return null; }
                },
                'GENESIS': () => {
                    try { return require('@/assets/img/provider_logos/GENESIS.webp'); } catch { return null; }
                },
                'KAGAMING': () => {
                    try { return require('@/assets/img/provider_logos/KAGAMING.webp'); } catch { return null; }
                },
                'MASCOTGAMING': () => {
                    try { return require('@/assets/img/provider_logos/MASCOTGAMING.webp'); } catch { return null; }
                },
                'RELAX': () => {
                    try { return require('@/assets/img/provider_logos/RELAX.webp'); } catch { return null; }
                },
                'SPRIBE': () => {
                    try { return require('@/assets/img/provider_logos/SPRIBE.webp'); } catch { return null; }
                }
            };
            
            const code = providerCode.toUpperCase();
            // Try exact match first
            if (iconMap[code]) {
                const icon = iconMap[code]();
                if (icon) return icon;
            }
            
            // Try partial match (e.g., PRAGMATIC_PLAY -> PRAGMATIC)
            for (const [key, getIcon] of Object.entries(iconMap)) {
                if (code.includes(key) || key.includes(code)) {
                    const icon = getIcon();
                    if (icon) return icon;
                }
            }
            
            return null;
        },
        
        getProviderInitials(providerName) {
            if (!providerName) return '?';
            
            // Clean the name
            let name = providerName.trim().toUpperCase();
            
            // Handle common provider name patterns
            const commonPatterns = {
                'PRAGMATIC': 'PG',
                'HABANERO': 'HB',
                'BOOONGO': 'BG',
                'PLAYSON': 'PS',
                'DREAMTECH': 'DT',
                'GAMEART': 'GA',
                'GMW': 'GMW',
                'PLAYSTAR': 'PS',
                'MICROGAMING': 'MG',
                'REDTIGER': 'RT',
                'NAGA': 'NG',
                'BTG': 'BTG',
                'QUICKSPIN': 'QS',
                'INOUT': 'IO',
                'CQ9': 'CQ9',
                'EVOPLAY': 'EP',
                'PGSOFT': 'PG',
                'BGAMING': 'BG',
                'GENESIS': 'GN',
                'KAGAMING': 'KG',
                'MASCOTGAMING': 'MG',
                'RELAX': 'RX',
                'SPRIBE': 'SB',
                'NETENT': 'NE',
                'PLAYNGO': 'PN',
                'THUNDERKICK': 'TK',
                'ELK': 'ELK',
                'PUSHGAMING': 'PG',
                'WAZDAN': 'WZ',
                'EVOLUTION': 'EV',
                'HACKSAW': 'HS',
                'NOLIMIT': 'NL',
                'YGGDRASIL': 'YG'
            };
            
            // Check for exact match first
            if (commonPatterns[name]) {
                return commonPatterns[name];
            }
            
            // Check for partial matches
            for (const [key, value] of Object.entries(commonPatterns)) {
                if (name.includes(key) || key.includes(name)) {
                    return value;
                }
            }
            
            // Handle multi-word names
            const words = name.split(/\s+/).filter(w => w.length > 0);
            if (words.length >= 2) {
                // Take first letter of first word and first letter of last word
                return (words[0][0] + words[words.length - 1][0]).substring(0, 2);
            }
            
            // Handle single word names - take first 2-3 letters intelligently
            if (name.length <= 3) {
                return name;
            }
            
            // For longer names, try to create meaningful abbreviation
            // Take first letter, then find next consonant
            let result = name[0];
            for (let i = 1; i < name.length && result.length < 2; i++) {
                const char = name[i];
                // Skip vowels unless it's the only option
                if (!['A', 'E', 'I', 'O', 'U'].includes(char) || i === name.length - 1) {
                    result += char;
                }
            }
            
            return result.substring(0, 3).toUpperCase();
        },
        
        handleProviderIconError(event) {
            // Hide broken image and show placeholder
            event.target.style.display = 'none';
            const placeholder = event.target.parentElement.querySelector('.provider-icon-placeholder');
            if (placeholder) {
                placeholder.style.display = 'flex';
            }
        },
        
        handleProviderIconLoad(event) {
            // Hide placeholder when icon loads successfully
            const placeholder = event.target.parentElement.querySelector('.provider-icon-placeholder');
            if (placeholder) {
                placeholder.style.display = 'none';
            }
        },

        syncProviderFromRoute() {
            const { providerCode } = this.$route.params;
            const path = this.$route.path || '';
            const isProviderPage = path.startsWith('/slots/provider/') && providerCode;
            const isMainSlots = path === '/slots' || (path.startsWith('/slots') && !providerCode);

            if (isProviderPage) {
                this.selectedProviderFilter = providerCode || '';
            } else if (isMainSlots) {
                this.selectedProviderFilter = '';
            }
        },

        handleClickOutsideProviderDropdown(e) {
            const el = this.$el;
            if (el && !el.contains(e.target)) {
                this.providerDropdownOpen = false;
            }
        },

        handleScroll() {
            // Check both window and main element scroll
            const mainElement = document.querySelector('main');
            const mainScrollTop = mainElement ? mainElement.scrollTop : 0;
            const windowScrollTop = window.scrollY || window.pageYOffset || 0;
            
            // Show button only when scrolled down at least 300px for better UX
            // This ensures the button doesn't appear unnecessarily
            this.showScrollTop = mainScrollTop > 300 || windowScrollTop > 300;
        },

        scrollToTop() {
            // Scroll the main element (which contains the page content)
            const mainElement = document.querySelector('main');
            if (mainElement) {
                mainElement.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                });
            }
            // Also try window scroll as fallback
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
            document.documentElement.scrollTop = 0;
            document.body.scrollTop = 0;
        },

        formatPrice(price) {
            if (!price) return 'N/A';
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 2
            }).format(price);
        },

        setDefaultProviderLogo(event) {
            event.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"%3E%3Crect fill="%23333" width="100" height="100"/%3E%3C/svg%3E';
        },

        closeGame() {
            this.resetGameSession();
        },
        handleGameModalKeydown(e) {
            if (e.key === 'Escape' && this.gameSession) {
                this.closeGame();
            }
        },

        launchGame(gameData) {
            // Close sidebar if it's open (not collapsed)
            // The game session is already set by the SlotsGameCard component
            // We just need to close the sidebar if it's open
            this.closeSidebarIfOpen();
        },

        closeSidebarIfOpen() {
            // Check if sidebar is open (not collapsed)
            const sidebar = document.querySelector('aside#sidebar-left');
            if (sidebar && !sidebar.classList.contains('collapsed')) {
                // Sidebar is open, close it by clicking the toggle button
                // Only close on desktop (width > 1024px)
                if (window.innerWidth > 1024) {
                    const toggleButton = sidebar.querySelector('.menu-toggle');
                    if (toggleButton) {
                        toggleButton.click();
                    }
                }
            }
            // If sidebar is already collapsed (closed), do nothing
        },

        // Setup real-time balance updates via Socket.io
        setupSocketListeners() {
            const socket = this.socketGeneral;
            
            if (!socket) {
                console.error('❌ Socket is not available');
                return;
            }

            // Ensure socket is connected
            if (!socket.connected) {
                console.warn('⚠️ Socket not connected, attempting to connect...');
                
                // Connect the socket if not already connected
                this.$store.dispatch('socketConnectGeneral');
                
                // Wait for connection then setup listener
                socket.once('connect', () => {
                    console.log('✅ Socket connected, setting up balance listener');
                    this.setupBalanceListener(socket);
                });
                
                return;
            }

            console.log('✅ Socket already connected, setting up balance listener');
            this.setupBalanceListener(socket);
        },

        setupBalanceListener(socket) {
            console.log('🔌 Setting up casino balance listener on general socket');
            console.log('🔌 Socket connected:', socket.connected);
            console.log('🔌 Socket ID:', socket.id);

            // Remove any existing listener to prevent duplicates
            socket.off('casino:balance-update');

            // Listen for casino balance updates from backend callbacks
            socket.on('casino:balance-update', (update) => {
                console.log('💰 Received balance update:', update);
                console.log('💰 New balance:', update.newBalance);
                console.log('💰 Transaction type:', update.transactionType);
                this.handleBalanceUpdate(update);
            });

            // Store unsubscribe function for cleanup
            this.socketUnsubscribe = () => {
                console.log('🔌 Unsubscribing from casino balance updates');
                socket.off('casino:balance-update');
            };
        }
    },

    watch: {
        authenticated(newVal) {
            if (newVal) {
                this.fetchUserBalance();
                this.setupSocketListeners();
            }
        },
        gameSession(newVal) {
            if (newVal) {
                document.body.classList.add('slot-game-playing');
                document.addEventListener('keydown', this.handleGameModalKeydown);
            } else {
                document.body.classList.remove('slot-game-playing');
                document.removeEventListener('keydown', this.handleGameModalKeydown);
            }
        }
    }
};
</script>

<style scoped>
.slots-container {
    width: 100%;
    min-height: calc(100vh - 80px);
    padding: 28px 32px 40px;
}

.slots-banner-section {
    width: 100%;
    position: relative;
    display: flex;
    align-items: center;
    margin-bottom: 20px;
    /*padding: 0 100px;*/
}

.slots-banner {
    width: 100%;
    max-width: 100%;
    height: auto;
    display: block;
    object-fit: cover;
    position: relative;
    z-index: 1;
}

.slots-title-container {
    position: absolute;
/*left: 100px;*/
    top: 0;
    z-index: 2;
    display: flex;
    align-items: baseline;
    gap: 20px;
    flex-wrap: nowrap;
    margin: 90px 0 0 45px;
}

.slots-title {
    font-family: 'Anton SC', sans-serif;
    font-weight: 400;
    font-style: normal;
    font-size: 64px;
    line-height: 69px;
    letter-spacing: 0%;
    color: var(--gradient-txt-yellow);
    background: var(--gradient-txt-yellow);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin: 0;
    padding: 0;
    white-space: nowrap;
    display: inline-block;
}

.slots-subtitle {
    font-family: 'Anton', sans-serif;
    font-weight: 400;
    font-style: normal;
    font-size: 24px;
    line-height: 100%;
    letter-spacing: 0%;
    color: #ffffff;
    margin: 0;
    padding: 0;
    white-space: nowrap;
    display: inline-block;
}

.loading-state,
.error-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 400px;
    gap: var(--spacing-lg);
}

.loading-spinner-simple {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
}

.loading-spinner-simple svg {
    color: var(--accent-yellow);
}

.loading-text-simple {
    font-size: 16px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.7);
    margin: 0;
    text-align: center;
}

.error-message {
    color: #ff6b6b;
    font-size: 16px;
    text-align: center;
}

.slots-content {
    max-width: 1400px;
    margin: 0 auto;
}

.slots-payment-section {
    width: 100%;
}

.slots-payment-section :deep(.payment-section) {
    margin-left: 0;
    margin-right: 0;
}

.providers-section,
.prices-section {
    margin-bottom: var(--spacing-2xl);
}

.games-section {
    margin-bottom: var(--spacing-2xl);
    border-radius: 20px;
    padding: 20px 24px 24px;
}

/* ── Top filter bar: Search + Provider pill ─────────────────────── */
.slots-filter-bar {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 24px;
    flex-wrap: wrap;
}

.slots-filter-bar .search-section {
    flex: 1;
    min-width: 180px;
    margin-bottom: 0;
}

.provider-filter-wrap {
    flex-shrink: 0;
}

.provider-dropdown {
    position: relative;
}

.provider-dropdown-trigger {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 11px 16px;
    min-width: 180px;
    border-radius: 12px;
    font-size: 14px;
    font-weight: 600;
    color: #ffffff;
    background: rgba(255, 255, 255, 0.07);
    border: 1.5px solid rgba(255, 255, 255, 0.18);
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s;
    text-align: left;
    height: 50px;
    box-sizing: border-box;
}

.provider-dropdown-trigger:hover {
    background: rgba(255, 255, 255, 0.12);
    border-color: rgba(255, 255, 255, 0.28);
}

.provider-label {
    color: rgba(255, 255, 255, 0.6);
    font-size: 12px;
    font-weight: 500;
}

.provider-value {
    flex: 1;
    font-weight: 700;
    color: #ffffff;
    font-size: 14px;
}

.provider-caret {
    flex-shrink: 0;
    color: rgba(255, 255, 255, 0.6);
    transition: transform 0.2s ease;
}

.provider-dropdown.open .provider-caret {
    transform: rotate(180deg);
}

.provider-dropdown-menu {
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    min-width: 100%;
    max-height: 320px;
    overflow-y: auto;
    background: #1a1d2e;
    border: 1.5px solid rgba(255, 255, 255, 0.15);
    border-radius: 12px;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
    z-index: 100;
}

.provider-dropdown-item {
    padding: 12px 16px;
    font-size: 14px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.9);
    cursor: pointer;
    transition: background 0.15s ease;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.provider-dropdown-item:last-child {
    border-bottom: none;
}

.provider-dropdown-item:hover,
.provider-dropdown-item.active {
    background: rgba(255, 255, 255, 0.1);
    color: #ffffff;
}

/* Provider cards - first view on Slots page */
.providers-heading {
    font-size: 28px;
    font-weight: 800;
    margin: 0 0 var(--spacing-xl) 0;
    background: var(--accent-main-txt);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    letter-spacing: 1px;
}

.providers-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: var(--spacing-lg);
    max-width: 1400px;
    margin: 0 auto;
}

.provider-card {
    background: #1a1a2e;
    border: 2px solid #2d2d44;
    border-radius: 12px 12px 16px 16px;
    overflow: hidden;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    flex-direction: column;
    height: 200px;
}

.provider-card:hover {
    border-color: #3d3d54;
    transform: translateY(-6px);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.5);
}

.provider-card-top {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: var(--spacing-lg);
    background: linear-gradient(135deg, rgba(72, 58, 160, 0.3) 0%, rgba(55, 54, 125, 0.4) 100%);
    gap: 12px;
    position: relative;
    overflow: hidden;
}

.provider-card-top::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at center, rgba(255, 255, 255, 0.05) 0%, transparent 70%);
    pointer-events: none;
}

.provider-icon-wrapper {
    width: 100px;
    height: 100px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 12px;
    flex-shrink: 0;
    position: relative;
    z-index: 1;
}

.provider-icon {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 12px;
    background: #ffffff;
    padding: 16px;
    box-sizing: border-box;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15), 
                0 1px 4px rgba(0, 0, 0, 0.1),
                inset 0 1px 0 rgba(255, 255, 255, 0.9);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    filter: brightness(1) contrast(1.02) saturate(1.05);
    border: 1px solid rgba(0, 0, 0, 0.05);
}

.provider-card:hover .provider-icon {
    transform: scale(1.08);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2), 
                0 2px 8px rgba(0, 0, 0, 0.15),
                inset 0 1px 0 rgba(255, 255, 255, 0.95);
    filter: brightness(1.02) contrast(1.05) saturate(1.08);
    border-color: rgba(0, 0, 0, 0.08);
}

.provider-icon-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #4a5568 0%, #2d3748 100%);
    border-radius: 12px;
    border: 2px solid rgba(255, 255, 255, 0.15);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3), 
                0 2px 8px rgba(0, 0, 0, 0.2),
                inset 0 1px 0 rgba(255, 255, 255, 0.1);
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
}

.provider-icon-placeholder::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, transparent 100%);
    pointer-events: none;
}

.provider-card:hover .provider-icon-placeholder {
    transform: scale(1.05);
    border-color: rgba(255, 255, 255, 0.25);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4), 
                0 4px 12px rgba(0, 0, 0, 0.3),
                inset 0 1px 0 rgba(255, 255, 255, 0.15);
    background: linear-gradient(135deg, #556175 0%, #374151 100%);
}

.provider-icon-text {
    font-size: 24px;
    font-weight: 800;
    color: #ffffff;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.6),
                 0 1px 4px rgba(0, 0, 0, 0.4),
                 0 0 12px rgba(255, 255, 255, 0.1);
    letter-spacing: 1.5px;
    font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif;
    position: relative;
    z-index: 1;
    text-transform: uppercase;
    line-height: 1;
}

.provider-card-name {
    font-size: 18px;
    font-weight: 800;
    color: #ffffff;
    text-align: center;
    line-height: 1.3;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    margin-top: auto;
}

.provider-card-play {
    background: var(--accent-btn-purple);
    border: none;
    border-radius: 0 0 16px 16px;
    color: #ffffff;
    cursor: pointer;
    font-size: 15px;
    font-weight: 800;
    padding: 14px 18px;
    transition: all 0.3s ease;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    width: 100%;
    margin: 0;
    text-align: center;
    height: 52px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.provider-card-play:hover {
    background: var(--gradient-purple);
}

.provider-info {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
}

.game-count {
    font-size: 13px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.7);
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.1);
    padding: 8px 14px;
    border-radius: 100px;
}

.games-section-top {
    margin-bottom: var(--spacing-md);
}

.back-to-slots-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    border: 2px solid var(--bg-blue-dark);
    border-radius: var(--radius-lg);
    color: #ffffff;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
}

.back-to-slots-btn:hover {
    
    background: var(--accent-btn-purple);
    border-color: var(--color-copper);
    color: var(--color-copper);
}

.back-to-slots-btn svg {
    flex-shrink: 0;
}

.section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: var(--spacing-lg);
}

.section-title {
    font-size: 28px;
    font-weight: 700;
    text-transform: none;
    color: #ffffff;
}

.games-grid-wrapper {
    width: 100%;
    padding: 0;
    background: transparent;
}

.games-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(175px, 1fr));
    gap: 16px;
    width: 100%;
}

.show-more-section {
    display: flex;
    justify-content: center;
    padding: var(--spacing-md) 0;
}

.show-more-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 12px 24px;
    font-size: 15px;
    font-weight: 600;
    color: #fff;
    background: var(--accent-btn-purple, #4a2c7a);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 12px;
    cursor: pointer;
    transition: background 0.2s, transform 0.15s, box-shadow 0.2s;
}

.show-more-btn:hover {
    background: var(--accent-btn-light-purple, #5a3c8a);
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
}

.show-more-count {
    font-size: 13px;
    opacity: 0.9;
}

.no-providers,
.no-games {
    text-align: center;
    padding: var(--spacing-2xl);
    color: rgba(255, 255, 255, 0.6);
}

.prices-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: var(--spacing-lg);
}

.price-card {
    background: var(--accent-blue-dark);
    border: 1px solid var(--accent-yellow);
    border-radius: var(--radius-lg);
    padding: var(--spacing-lg);
    display: flex;
    flex-direction: column;
    gap: var(--spacing-md);
}

.price-label {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.7);
}

.price-value {
    font-size: 20px;
    font-weight: 700;
    color: var(--color-copper);
}

/* Game modal transition */
.game-modal-fade-enter-active,
.game-modal-fade-leave-active {
    transition: opacity 0.25s ease;
}
.game-modal-fade-enter-active .game-modal,
.game-modal-fade-leave-active .game-modal {
    transition: transform 0.25s ease;
}
.game-modal-fade-enter-from,
.game-modal-fade-leave-to {
    opacity: 0;
}
.game-modal-fade-enter-from .game-modal,
.game-modal-fade-leave-to .game-modal {
    transform: scale(0.97);
}

/* Slot game modal: full screen */
.game-modal-overlay {
    position: fixed !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    width: 100vw !important;
    height: 100vh !important;
    background: #000;
    display: flex;
    align-items: stretch;
    justify-content: stretch;
    z-index: 9999999 !important;
    padding: 0 !important;
    margin: 0 !important;
    box-sizing: border-box !important;
}

.game-modal {
    background: #000;
    border: none;
    border-radius: 0;
    width: 100% !important;
    height: 100% !important;
    max-width: none !important;
    max-height: none !important;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    position: relative;
    z-index: 10000000 !important;
}

/* Header hidden so slot iframe is never covered – close via floating button only */
.game-modal-header {
    display: none !important;
}

.game-modal-title {
    font-size: 17px;
    font-weight: 600;
    color: #fff;
    letter-spacing: 0.02em;
}

.game-modal-close {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 14px;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 10px;
    color: rgba(255, 255, 255, 0.9);
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
}

.game-modal-close:hover {
    background: rgba(255, 255, 255, 0.12);
    border-color: rgba(255, 255, 255, 0.2);
    color: #fff;
}

.game-modal-close:focus-visible {
    outline: 2px solid var(--color-copper);
    outline-offset: 2px;
}

.game-modal-close svg {
    flex-shrink: 0;
}

.game-modal-close-text {
    display: none;
}

@media (min-width: 480px) {
    .game-modal-close-text {
        display: inline;
    }
}

/* Content fills entire modal – header overlays on top */
.game-modal-content {
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    min-height: 0;
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    z-index: 1;
}

/* Floating close – always visible and clickable above iframe */
.game-modal-close-floating {
    position: absolute !important;
    top: 12px !important;
    right: 12px !important;
    width: 48px !important;
    height: 48px !important;
    display: flex !important;
    align-items: center;
    justify-content: center;
    padding: 0 !important;
    background: rgba(0, 0, 0, 0.6) !important;
    border: 2px solid rgba(255, 255, 255, 0.25) !important;
    border-radius: 50% !important;
    color: #fff !important;
    cursor: pointer !important;
    z-index: 10000003 !important;
    pointer-events: auto !important;
    transition: background 0.2s ease, border-color 0.2s ease, transform 0.15s ease;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
}

.game-modal-close-floating:hover {
    background: rgba(0, 0, 0, 0.85) !important;
    border-color: rgba(255, 255, 255, 0.5) !important;
    transform: scale(1.05);
}

.game-modal-close-floating:focus-visible {
    outline: 2px solid var(--color-copper);
    outline-offset: 2px;
}

@media (max-width: 1024px) {
    .slots-banner-section {
        padding: 0 var(--spacing-md);
        margin-bottom: 15px;
    }

    .slots-title-container {
        left: var(--spacing-md);
        gap: 15px;
    }

    .slots-title {
        font-size: 48px;
        line-height: 52px;
    }

    .slots-subtitle {
        font-size: 18px;
    }

    .game-modal-overlay {
        padding: 0 !important;
    }
}

@media (max-width: 768px) {
    .slots-container {
        padding: 0;
        margin-top: 54px;
    }

    .slots-banner-section {
        padding: 0 var(--spacing-sm);
        margin-bottom: 10px;
    }

    .slots-title-container {
        left: var(--spacing-sm);
        gap: 12px;
        margin: 30px 0 0 5px;
    }

    .slots-title {
        font-size: 36px;
        line-height: 40px;
    }

    .slots-subtitle {
        font-size: 16px;
    }

    .game-modal-overlay {
        padding: 0 !important;
    }

    .game-modal-title {
        font-size: 15px;
    }

    .providers-grid,
    .games-grid {
        grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
        gap: var(--spacing-sm);
    }

    .section-title {
        font-size: 22px;
    }

    .slots-content {
        padding: var(--spacing-sm);
    }

    .providers-heading {
        font-size: 22px;
    }

    .provider-card {
        height: 180px;
    }
    
    .provider-icon-wrapper {
        width: 80px;
        height: 80px;
    }
    
    .provider-icon {
        padding: 10px;
    }
    
    .provider-icon-text {
        font-size: 24px;
    }

    .provider-card-name {
        font-size: 16px;
    }

    .provider-card-play {
        height: 46px;
        border-radius: 0 0 14px 14px;
    }

    .search-input-wrapper {
        max-width: 100%;
    }
}

@media (max-width: 480px) {
    .slots-container {
        padding: 0;
        margin-top: 64px;
    }

    .slots-banner-section {
        padding: 0 var(--spacing-xs);
        margin-bottom: 10px;
    }

    .slots-title-container {
        left: var(--spacing-xs);
        gap: 10px;
        margin: 30px 0 0 5px;
    }

    .slots-title {
        font-size: 28px;
        line-height: 32px;
    }

    .slots-subtitle {
        font-size: 14px;
    }

    .game-modal-overlay {
        padding: 0 !important;
    }

    .game-modal-title {
        font-size: 14px;
    }

    .providers-grid {
        grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
        gap: var(--spacing-sm);
    }

    .games-grid {
        grid-template-columns: repeat(2, 1fr);
        gap: 12px;
    }

    .games-section {
        padding: 12px;
    }

    .providers-heading {
        font-size: 18px;
    }

    .provider-card {
        height: 160px;
    }
    
    .provider-icon-wrapper {
        width: 56px;
        height: 56px;
        margin-bottom: 6px;
    }
    
    .provider-icon-text {
        font-size: 18px;
    }

    .provider-card-name {
        font-size: 14px;
    }

    .provider-card-play {
        height: 42px;
        border-radius: 0 0 12px 12px;
    }

    .section-title {
        font-size: 18px;
    }

    .balance-display {
        padding: var(--spacing-md);
    }
}

/* Search Section */
.search-section {
    margin-bottom: var(--spacing-lg);
}

.search-input-wrapper {
    position: relative;
    max-width: 400px;
}

.search-icon {
    position: absolute;
    left: 16px;
    top: 50%;
    transform: translateY(-50%);
    color: #6b7c8a;
    pointer-events: none;
}

.search-input {
    width: 100%;
    height: 50px;
    padding: 0 48px 0 48px;
    border-radius: 12px;
    font-size: 15px;
    font-weight: 500;
    color: #ffffff;
    background: rgba(255, 255, 255, 0.06);
    border: 1.5px solid rgba(255, 255, 255, 0.14);
    outline: none;
    transition: border-color 0.2s, background 0.2s;
    font-family: var(--font-main);
}

.search-input:focus {
    border-color: var(--color-copper, #d4a574);
    background: rgba(255, 255, 255, 0.09);
}

.search-input::placeholder {
    color: rgba(255, 255, 255, 0.35);
}

.search-clear {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 50%;
    color: #ffffff;
    cursor: pointer;
    transition: all 0.3s ease;
}

.search-clear:hover {
    background: rgba(255, 255, 255, 0.2);
}

/* Scroll to Top Button */
.scroll-to-top {
    position: fixed !important;
    bottom: 30px !important;
    right: 30px !important;
    width: 56px !important;
    height: 56px !important;
    display: flex !important;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: 50%;
    background: linear-gradient(180deg, #ffd700 0%, #fca311 50%, #e69500 100%);
    color: #000000;
    cursor: pointer;
    box-shadow: 0 4px 20px rgba(252, 163, 17, 0.5);
    transition: all 0.3s ease;
    z-index: 999 !important; /* Lower z-index to stay below modals */
    opacity: 0.9;
}

.scroll-to-top:hover {
    transform: translateY(-5px) scale(1.05);
    box-shadow: 0 8px 25px rgba(252, 163, 17, 0.6);
    opacity: 1;
}

.scroll-to-top svg {
    width: 28px;
    height: 28px;
}

@media (max-width: 768px) {
    .search-input-wrapper {
        max-width: 100%;
    }

    .scroll-to-top {
        bottom: 90px;
        right: 20px;
        width: 50px;
        height: 50px;
    }
}

@media (max-width: 450px) {
    .scroll-to-top {
        bottom: 100px;
        right: 15px;
        width: 48px;
        height: 48px;
    }
}
</style>
